import { logger } from '@heidi/runtime/logger';
import { TemplateCategory } from '@heidi/schema/ml-scribe';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type CreateTemplateProps,
  type TemplateResponse,
  GET_CONSULT_NOTE_TEMPLATES,
  GET_DOCUMENT_TEMPLATES,
  GET_TEMPLATES,
} from '../../contracts';
import { createTemplate } from '../api/templates';

/**
 * The mutation's variables, extended by whatever the caller carries for
 * analytics.
 *
 * `creationSource` / `creationAiCommandText` are ANALYTICS-ONLY — they never
 * reach the request body. Their union type (`TemplateCreationSource`) lives in
 * the app's analytics model, which this package must not import, and widening
 * them to `string` here would silently drop that union at all four call sites.
 * So the extension crosses as a type parameter: the delegate binds it to the
 * app type and every caller keeps exactly the typing it had.
 */
export type CreateTemplateVariables<TAnalytics extends object = object> = {
  input: CreateTemplateProps;
  saveAnyway: boolean;
  signal?: AbortSignal;
} & TAnalytics;

type UseMutateCreateTemplateProps<TAnalytics extends object> = {
  /**
   * Fires after the three template lists are invalidated and before nothing —
   * it is the last statement of `onSuccess`. Everything the legacy hook did
   * from here on was an app reaction: two cross-domain invalidations (user,
   * gamify-trial), three analytics events, an atom write behind the toast's
   * action button, and the toast itself.
   */
  onCreated?: (
    template: TemplateResponse,
    variables: CreateTemplateVariables<TAnalytics>
  ) => void;
  /**
   * Fires in `onError`. The legacy body was a single call to
   * `handleTemplateComplianceError`, which needs `dispatch`, `intl`, `toast`
   * and three modal atoms — an app reaction end to end, so the whole branch
   * crosses rather than any part of it moving.
   */
  onCreateFailed?: (
    error: unknown,
    variables: CreateTemplateVariables<TAnalytics>
  ) => void;
};

/**
 * Creates a template and refreshes the three lists it can appear in.
 *
 * Headless. `isOrganizationTemplateManager` does not appear here at all: the
 * only thing the legacy hook did with it was branch one analytics dispatch,
 * and that dispatch is an app reaction — so the branch went app-side with it.
 * That is the S5 correction in its strongest form: not only was this hook not
 * waiting for a workspace provider, it does not need the value.
 */
export function useMutateCreateTemplate<TAnalytics extends object = object>({
  onCreated,
  onCreateFailed,
}: UseMutateCreateTemplateProps<TAnalytics> = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['createTemplate'],
    mutationFn: async ({
      input,
      saveAnyway,
      signal,
    }: CreateTemplateVariables<TAnalytics>) => {
      // Rides along from the legacy hook, re-pointed at the runtime logger —
      // the app logger is an `@/` import the package cannot make.
      logger.debug('Template creation input:', input);

      const data = await createTemplate({
        template: {
          ...input,
          trigger: 'from_scratch',
          template_category:
            input.template_category || TemplateCategory.DOCUMENT_TEMPLATE,
          template_visibility: input.template_visibility,
          team_id: input.team_id,
          template_gallery_sharing_preference_show_clinic:
            input.template_gallery_sharing_preference_show_clinic,
          template_gallery_sharing_preference_show_name:
            input.template_gallery_sharing_preference_show_name,
          template_gallery_sharing_preference_show_profile_picture:
            input.template_gallery_sharing_preference_show_profile_picture,
          // `isEmpty` (lodash-es) cannot enter package source — the package
          // jest preset is transform-only and the dep is ESM-only. Both fields
          // are typed `string | undefined`, and for those two types `isEmpty`
          // and `!value` agree on every input, including `'0'`.
          template_html: !input.template_html ? undefined : input.template_html,
          structure_template: !input.structure_template
            ? undefined
            : input.structure_template,
          sectional_note_ehr_provider: input?.sectional_note_ehr_provider,
          version_control_id: input?.version_control_id || undefined,
          shared_team_ids: input?.shared_team_ids || undefined,
          convert_to_json: input?.convert_to_json ?? false,
          original_v1_structure_template: input?.original_v1_structure_template,
          template_creation_record_id: input?.template_creation_record_id || undefined,
          problem_based_charting_enabled: input?.problem_based_charting_enabled ?? false,
          // Pass `undefined` through so callers can defer to the backend
          // default by omitting the field.
          use_sections: input?.use_sections,
        },
        saveAnyway,
        signal,
      });

      return data;
    },

    onSuccess: async ({ data: template }, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [GET_TEMPLATES] }),
        queryClient.invalidateQueries({ queryKey: [GET_DOCUMENT_TEMPLATES] }),
        queryClient.invalidateQueries({ queryKey: [GET_CONSULT_NOTE_TEMPLATES] }),
      ]);
      onCreated?.(template, variables);
    },

    onError: (error, variables) => {
      onCreateFailed?.(error, variables);
    },
  });
}
